import type { GetTelemetryHistoryResponse } from '@eppendorf/vnls-device-service-utils';
import { useQuery } from '@tanstack/react-query';

import { fetcher } from '$shared/fetch';
import { REPORTING_SERVICE_BASE_PATH } from '$shared/utils/api-paths';

export const stabilityReportChartsQueryKey = 'stabilityReportCharts';
const stabilityReportHistoryQueryKey = 'stabilityReportHistory';

const normalizeTelemetryKey = (value?: string | null) =>
  value?.toLowerCase().replace(/[^a-z\d]/g, '') ?? '';

/**
 * Shared query for the full history data array.
 * The backend reads from S3 and returns the JSON directly.
 */
export const useStabilityReportHistory = (reportId: string) =>
  useQuery<GetTelemetryHistoryResponse[]>({
    queryKey: [stabilityReportHistoryQueryKey, reportId],
    queryFn: () =>
      fetcher.get<GetTelemetryHistoryResponse[]>(
        `${REPORTING_SERVICE_BASE_PATH}/${reportId}/history`,
      ),
    enabled: Boolean(reportId),
  });

/**
 * Returns the chart data for a single parameter by matching
 * telemetryFunctionName or telemetryId against the parameter name from insights.
 * New reports use stable telemetry IDs; archived reports fall back to names.
 */
export const useGetStabilityReportChart = (
  reportId: string,
  parameterName: string,
  telemetryId?: string,
) => {
  const { data: allHistory, isLoading, isError } = useStabilityReportHistory(reportId);

  const normalizedParameterName = normalizeTelemetryKey(parameterName);

  const parameterData = telemetryId
    ? allHistory?.find((sensor) => sensor.telemetryId === telemetryId)
    : allHistory?.find(
        (sensor) =>
          sensor.telemetryFunctionName === parameterName ||
          sensor.telemetryId === parameterName ||
          normalizeTelemetryKey(sensor.telemetryFunctionName) ===
            normalizedParameterName ||
          normalizeTelemetryKey(sensor.telemetryId) === normalizedParameterName,
      );

  return {
    data: parameterData,
    isLoading,
    isError,
  };
};
